import { Button } from '@vivaldis/antd-ui'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ActionBar as BaseActionBar } from '../../../ActionBar'
import { SubmitButton } from '../../../SubmitButton'
import { WorkPostSheetFormStep } from '../../constants/wpsFormSteps'
import { useForm } from '../../providers/FormProvider'
import { useStep } from '../../providers/StepProvider'
import { getFirstWPSFormStepWithError } from '../../utils/getFirstWPSFormStepWithError'

interface Props {
  buttonText: string
}

export const ActionBar = ({ buttonText }: Props) => {
  const [t] = useTranslation('web_ui')

  const { values, isSubmitting, submitForm, validateForm } = useForm()
  const { step, setStep } = useStep()

  // Validates the whole form. When invalid, opens the first step with an error and returns true.
  const goToFirstStepWithError = useCallback(async () => {
    const errors = await validateForm()
    const stepWithError = getFirstWPSFormStepWithError(errors, values)

    if (stepWithError === undefined) {
      return false
    }

    setStep(stepWithError)
    // the form is invalid, so this doesn't call onSubmit: it only marks fields as touched
    // and triggers the scroll to the first field with an error on the opened step
    await submitForm()
    return true
  }, [setStep, submitForm, validateForm, values])

  const handlePreviousClick = useCallback(() => {
    setStep(step => step - 1)
  }, [setStep])

  const handleNextClick = useCallback(async () => {
    // step 4 is the last step with inputs: validate everything before showing the overview
    if (step === WorkPostSheetFormStep.Step4) {
      if (await goToFirstStepWithError()) {
        return
      }
    }
    setStep(step => step + 1)
  }, [goToFirstStepWithError, setStep, step])

  const handleSubmitClick = useCallback(async () => {
    if (await goToFirstStepWithError()) {
      return
    }
    await submitForm()
  }, [goToFirstStepWithError, submitForm])

  // a custom PDF is a single step form
  const isSingleStep = values.uploadOwnWPS

  const showPreviousButton = !isSingleStep && step > WorkPostSheetFormStep.Step1
  const showSubmitButton =
    isSingleStep || step === WorkPostSheetFormStep.Overview

  return (
    <BaseActionBar
      leftContent={
        showPreviousButton ? (
          <Button
            shape="round"
            size="large"
            disabled={isSubmitting}
            onClick={handlePreviousClick}
          >
            {t('work_post_sheet_form.steps.previous_button')}
          </Button>
        ) : null
      }
      rightContent={
        showSubmitButton ? (
          <SubmitButton
            isSubmitting={isSubmitting}
            label={buttonText}
            // not a native submit: submitting is done in handleSubmitClick, so it doesn't run twice
            htmlType="button"
            onClick={handleSubmitClick}
          />
        ) : (
          <Button
            type="primary"
            shape="round"
            size="large"
            onClick={handleNextClick}
          >
            {t('work_post_sheet_form.steps.next_button')}
          </Button>
        )
      }
    />
  )
}
